import { useState, useEffect } from 'react'
import { Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { adminFieldLibraryService } from '../../services/admin.fieldLibrary.service'
import { FIELD_TYPES } from './FieldTypePicker'
import type { FieldLibraryDocument } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (libraryId: string) => Promise<void>
}

export function LibraryFieldPicker({ open, onOpenChange, onSelect }: Props) {
  const [fields, setFields] = useState<Array<FieldLibraryDocument & { id: string }>>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    adminFieldLibraryService
      .getAll()
      .then(setFields)
      .finally(() => setLoading(false))
  }, [open])

  const filtered = fields.filter(
    (f) =>
      f.label.toLowerCase().includes(search.toLowerCase()) ||
      f.fieldId.toLowerCase().includes(search.toLowerCase()) ||
      (f.category ?? '').toLowerCase().includes(search.toLowerCase())
  )

  async function handleSelect(id: string) {
    setAdding(id)
    try {
      await onSelect(id)
      onOpenChange(false)
    } finally {
      setAdding(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add from Field Library</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search fields…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="max-h-96 space-y-2 overflow-y-auto">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {fields.length === 0 ? 'Field library is empty.' : 'No matching fields.'}
            </p>
          ) : (
            filtered.map((field) => {
              const typeInfo = FIELD_TYPES.find((t) => t.type === field.type)
              const Icon = typeInfo?.icon
              return (
                <button
                  key={field.id}
                  type="button"
                  disabled={adding === field.id}
                  onClick={() => void handleSelect(field.id)}
                  className="flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors hover:bg-accent disabled:opacity-50"
                >
                  {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{field.label}</p>
                    <p className="font-mono text-xs text-muted-foreground">{field.fieldId}</p>
                  </div>
                  {field.category && (
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {field.category}
                    </Badge>
                  )}
                </button>
              )
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
